import React from 'react';
import { ErrorFallback } from './ErrorFallback';
import * as Sentry from '@sentry/react-native';
import { bumpLazyRetry } from '../../utils/lazyRetry';

interface ErrorBoundaryProps {
    children: React.ReactNode;
    /** Optional custom fallback. Defaults to <ScreenState status="error">. */
    fallback?: React.ReactNode;
    /** Optional callback for analytics / cleanup on error. */
    onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
    error: Error | null;
}

/**
 * App-level error boundary. Catches React render errors that would
 * otherwise crash the whole tree, reports them to Sentry, and shows
 * a themed retry screen.
 *
 * Mount at the app root (App.tsx) wrapping NavigationContainer so any
 * screen-level crash is contained.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { error: null };

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        // Sentry is wired in the codebase already (see App.tsx initialisation
        // and Sentry.wrap). Capturing here gives us a React-stack trace on
        // top of the JS one — handy for errors deep inside provider trees.
        try {
            Sentry.captureException(error, {
                contexts: {
                    react: { componentStack: info.componentStack ?? undefined },
                },
            });
        } catch {
            // Sentry init may not have run yet during a bootstrap-time crash.
            // Swallow — we still render the fallback below.
        }
        this.props.onError?.(error, info);
    }

    handleRetry = () => {
        // CORE-1517 — let failed lazy screens import again (React.lazy would
        // otherwise re-throw the remembered rejection).
        bumpLazyRetry();
        this.setState({ error: null });
    };

    render() {
        if (this.state.error) {
            if (this.props.fallback) return this.props.fallback;
            // CORE-1517 — retry, «На главную» on web, offline-aware; the raw
            // message (chunk URL and all) stays in Sentry.
            return <ErrorFallback error={this.state.error} onRetry={this.handleRetry} />;
        }
        return this.props.children;
    }
}
